import { currencyGet, ApiShapeError } from '@/lib/task-api';
import { parseCount, serverVolume } from '@/lib/report-volume';
import type { TotalAmount } from '@/lib/money';
import { pairOf } from '@/lib/task-currency';
import { byCurrencyOrder, type CurrencyTotal } from './api';

/**
 * ================= MUSTERI ORGANIZASYONU UCLARI (2026-08-30) ==================
 *
 * Uc AYRI uc, uc AYRI kapsam — karistirilmasi kolay oldugu icin tek dosyada:
 *
 *   GET /v1/dashboards/customer            -> CAGIRANIN KENDI verisi (herkes)
 *   GET /v1/reports/customer/org-summary   -> organizasyon geneli (YALNIZ yonetici)
 *   GET /v1/reports/customer/member-activity -> uye kirilimi   (YALNIZ yonetici)
 *
 * Panel bilincli olarak `viewReport` ISTEMEZ: rapor degil, kullanicinin kendi verisidir
 * (buro tarafindaki /v1/dashboards/exchange ile ayni mantik). Iki rapor ise sunucuda
 * `requireOrgAdmin` ile korunuyor — personelin org geneli toplamlari gormesi, diger
 * personelin hacmini dolayli olarak ifsa ederdi (KVKK / calisan izleme).
 *
 * ---------------------------- CIRO, KUR TOPLAMI DEGIL ------------------------
 * Buro raporlarindaki `offers-summary` `sum(offer_price)` doner ve bu BIRIM KURLARIN
 * TOPLAMIDIR — para birimi cinsinden bir ciro DEGIL (bkz. lib/report-volume.ts rateSum).
 * Buradaki uclar `sum(task_amount * offer_price)` hesapliyor, yani GERCEK hacim; bu yuzden
 * `serverVolume()` ile TotalAmount isaretleniyorlar ve <MoneyText total=…> icine gecebilirler.
 * Ikisini karistirmak DERLEME HATASI verir — markali tiplerin varlik sebebi bu.
 *
 * ------------------------- PARA BIRIMI (2026-09-23, karar Q13) ---------------
 * Ciro KARSI para biriminde: EUR/USD talebinde USD, digerlerinde TRY. Uc uc da artik birimi
 * soyluyor ve ekran farkli birimleri TEK sayida TOPLAMAZ:
 *   dashboards/customer      -> satir basina `counterCurrency`
 *   org-summary              -> satirlar (durum x counterCurrency); `turnover` o birimde
 *   member-activity          -> `turnoverByCurrency: {TRY, USD}`; `turnover` YALNIZ TRY
 * ========================================================================== */

type RawEnvelope = Record<string, unknown> & { code?: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const expectList = (data: RawEnvelope, field: string, endpoint: string): unknown[] => {
  const list = data[field];
  // Bos diziye DUSMUYOR: sozlesme ayrismasini "kayit yok" diye gostermek, gercek bir
  // hatayi normal bir bos ekrana cevirirdi.
  if (!Array.isArray(list)) throw new ApiShapeError(`${endpoint}.${field} dizi degil`);
  return list;
};

const expectRow = (raw: unknown, endpoint: string, index: number): Record<string, unknown> => {
  if (!isRecord(raw)) throw new ApiShapeError(`${endpoint}[${index}] nesne degil`);
  return raw;
};

const asCode = (value: unknown, field: string): string => {
  if (typeof value === 'string' && value !== '') return value;
  throw new ApiShapeError(`${field} bos olmayan bir dize degil`);
};

const asId = (value: unknown, field: string): string => {
  if (typeof value === 'string' && value !== '') return value;
  // pg int8'i string doner; kucuk kimliklerde number gorulebiliyor.
  if (typeof value === 'number') return String(value);
  throw new ApiShapeError(`${field} kimlik degil`);
};

/* -------------------------------------------------------------------------- */
/* Kisisel panel — GET /v1/dashboards/customer                                 */
/* -------------------------------------------------------------------------- */

const ENDPOINT_DASHBOARD = '/v1/dashboards/customer';

export type CustomerDashboardRow = Readonly<{
  statusCode: string;
  /** Para birimi / islem tipi kodu ('USD', 'EUR/USD'…). */
  typeCode: string;
  taskCount: number;
  /** Bir buronun teklifi kabul edilmis talepler. */
  matchedCount: number;
  turnover: TotalAmount;
  /** `turnover`in birimi (EUR/USD'de USD). */
  counterCurrency: string;
}>;

/** Sunucu birimi gondermediyse (eski surum) tipten turetilir; ayni kaynak: task_type. */
const counterCurrencyOf = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value !== '' ? value : fallback;

export const fetchCustomerDashboard = async (
  from: string | undefined,
  to: string | undefined,
): Promise<readonly CustomerDashboardRow[]> => {
  const { data } = await currencyGet<RawEnvelope>(ENDPOINT_DASHBOARD, {
    ...(from === undefined ? {} : { from }),
    ...(to === undefined ? {} : { to }),
  });
  return expectList(data, 'summaryList', ENDPOINT_DASHBOARD).map((raw, index) => {
    const row = expectRow(raw, `${ENDPOINT_DASHBOARD}.summaryList`, index);
    const typeCode = asCode(row.taskTypeCode, `${ENDPOINT_DASHBOARD}[${index}].taskTypeCode`);
    return {
      statusCode: asCode(row.taskStatusCode, `${ENDPOINT_DASHBOARD}[${index}].taskStatusCode`),
      typeCode,
      taskCount: parseCount(row.taskCount, `${ENDPOINT_DASHBOARD}[${index}].taskCount`),
      matchedCount: parseCount(row.matchedCount, `${ENDPOINT_DASHBOARD}[${index}].matchedCount`),
      turnover: serverVolume(row.turnover, `${ENDPOINT_DASHBOARD}[${index}].turnover`),
      counterCurrency: counterCurrencyOf(row.counterCurrency, pairOf(typeCode).secondCurrency),
    };
  });
};

/* -------------------------------------------------------------------------- */
/* Organizasyon ozeti — GET /v1/reports/customer/org-summary (yalniz yonetici)  */
/* -------------------------------------------------------------------------- */

const ENDPOINT_ORG_SUMMARY = '/v1/reports/customer/org-summary';

export type CustomerOrgSummaryRow = Readonly<{
  statusCode: string;
  /** Satir (durum x karsi para birimi); `turnover` bu birimde. */
  counterCurrency: string;
  taskCount: number;
  matchedCount: number;
  turnover: TotalAmount;
}>;

export const fetchCustomerOrgSummary = async (
  from: string | undefined,
  to: string | undefined,
): Promise<readonly CustomerOrgSummaryRow[]> => {
  const { data } = await currencyGet<RawEnvelope>(ENDPOINT_ORG_SUMMARY, {
    ...(from === undefined ? {} : { from }),
    ...(to === undefined ? {} : { to }),
  });
  return expectList(data, 'summaryList', ENDPOINT_ORG_SUMMARY).map((raw, index) => {
    const row = expectRow(raw, `${ENDPOINT_ORG_SUMMARY}.summaryList`, index);
    return {
      statusCode: asCode(row.taskStatusCode, `${ENDPOINT_ORG_SUMMARY}[${index}].taskStatusCode`),
      // Eski sunucu birim gondermiyordu ve satir durum basinaydi; o surumde tek birim TRY'ydi.
      counterCurrency: counterCurrencyOf(row.counterCurrency, 'TRY'),
      taskCount: parseCount(row.taskCount, `${ENDPOINT_ORG_SUMMARY}[${index}].taskCount`),
      matchedCount: parseCount(row.matchedCount, `${ENDPOINT_ORG_SUMMARY}[${index}].matchedCount`),
      turnover: serverVolume(row.turnover, `${ENDPOINT_ORG_SUMMARY}[${index}].turnover`),
    };
  });
};

/* -------------------------------------------------------------------------- */
/* Uye aktivitesi — GET /v1/reports/customer/member-activity (yalniz yonetici)  */
/* -------------------------------------------------------------------------- */

const ENDPOINT_MEMBER_ACTIVITY = '/v1/reports/customer/member-activity';

export type CustomerMemberActivityRow = Readonly<{
  appUserId: string;
  userFullName: string;
  memberTypeCode: string;
  isActive: boolean;
  taskCount: number;
  matchedCount: number;
  /** YALNIZ TRY karsilikli ciro (sunucunun geriye donuk alani). Ekran bunu DEGIL asagiyi cizer. */
  turnover: TotalAmount;
  /** Birim basina ciro, TRY once. Bos olabilir (donemde eslesen talep yok). */
  turnoverByCurrency: readonly CurrencyTotal[];
}>;

/**
 * `turnoverByCurrency` okumasi.
 *
 * ANAHTARLAR BUYUK HARFE CEVRILIR: sunucu satirlari lodash `camelCase` ile ic ice
 * donusturuyor (utils.js convertObjectToCamelCase) ve bu jsonb nesnesinin ANAHTARLARINA da
 * uygulaniyor — 'TRY' -> 'try', 'USD' -> 'usd' olarak geliyor. Para birimi kodlari ISO 4217
 * (hep buyuk harf), yani geri cevirmek kayipsiz.
 * Alan hic yoksa (eski sunucu) yalniz TRY bilinir.
 */
const toTurnoverByCurrency = (
  raw: unknown,
  turnover: TotalAmount,
  field: string,
): CurrencyTotal[] => {
  if (raw === undefined || raw === null) {
    return turnover > 0 ? [{ currency: 'TRY', total: turnover }] : [];
  }
  if (!isRecord(raw)) throw new ApiShapeError(`${field} nesne degil`);
  return Object.keys(raw)
    .map((key) => ({
      currency: key.toUpperCase(),
      total: serverVolume(raw[key], `${field}.${key}`),
    }))
    .filter((item) => item.total !== 0)
    .sort((a, b) => byCurrencyOrder(a.currency, b.currency));
};

export const fetchCustomerMemberActivity = async (
  from: string | undefined,
  to: string | undefined,
): Promise<readonly CustomerMemberActivityRow[]> => {
  const { data } = await currencyGet<RawEnvelope>(ENDPOINT_MEMBER_ACTIVITY, {
    ...(from === undefined ? {} : { from }),
    ...(to === undefined ? {} : { to }),
  });
  return expectList(data, 'activityList', ENDPOINT_MEMBER_ACTIVITY).map((raw, index) => {
    const row = expectRow(raw, `${ENDPOINT_MEMBER_ACTIVITY}.activityList`, index);
    return {
      appUserId: asId(row.appUserId, `${ENDPOINT_MEMBER_ACTIVITY}[${index}].appUserId`),
      userFullName: typeof row.userFullName === 'string' ? row.userFullName : '',
      memberTypeCode:
        typeof row.organizationMemberTypeCode === 'string' ? row.organizationMemberTypeCode : '',
      // Yalniz acik `false` pasif sayilir; alan hic gelmezse aktif varsayilir.
      isActive: row.memberIsActive !== false,
      taskCount: parseCount(row.taskCount, `${ENDPOINT_MEMBER_ACTIVITY}[${index}].taskCount`),
      matchedCount: parseCount(
        row.matchedCount, `${ENDPOINT_MEMBER_ACTIVITY}[${index}].matchedCount`,
      ),
      turnover: serverVolume(row.turnover, `${ENDPOINT_MEMBER_ACTIVITY}[${index}].turnover`),
      turnoverByCurrency: toTurnoverByCurrency(
        row.turnoverByCurrency,
        serverVolume(row.turnover, `${ENDPOINT_MEMBER_ACTIVITY}[${index}].turnover`),
        `${ENDPOINT_MEMBER_ACTIVITY}[${index}].turnoverByCurrency`,
      ),
    };
  });
};
